import { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import type { HistoricalValueItem } from '@/api/types';

type SortField = 'period' | 'value' | 'change' | 'changePercent';
type SortDirection = 'asc' | 'desc';

interface ExtendedHistoricalValueItem extends HistoricalValueItem {
    valueChange?: number | null;
    growthPercentage?: number | null;
}

interface HistoricalValueTableProps {
    /** Array of historical value items for table display */
    items: ExtendedHistoricalValueItem[];
    /** Display currency */
    currency: string;
    /** Whether this is a debt ledger (affects color coding) */
    isDebt?: boolean;
}

/**
 * Get change color based on value and ledger type.
 * For debts: positive (more debt) is bad, negative (less debt) is good.
 * For assets: positive is good, negative is bad.
 */
function getChangeColorClass(value: number | null | undefined, isDebt: boolean = false): string {
    if (value === null || value === undefined) return '';
    if (isDebt) {
        if (value > 0) return 'text-red-600 dark:text-red-400';
        if (value < 0) return 'text-green-600 dark:text-green-400';
    } else {
        if (value > 0) return 'text-green-600 dark:text-green-400';
        if (value < 0) return 'text-red-600 dark:text-red-400';
    }
    return 'text-gray-600 dark:text-gray-400';
}

/**
 * Format percentage with sign.
 */
function formatPercentage(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
}

/**
 * Sort indicator icon component.
 */
function SortIcon({ field, currentField, direction }: { field: SortField; currentField: SortField; direction: SortDirection }) {
    if (field !== currentField) {
        return (
            <span className="ml-1 inline-flex flex-col opacity-30">
                <ChevronUpIcon className="h-3 w-3 -mb-1" />
                <ChevronDownIcon className="h-3 w-3" />
            </span>
        );
    }
    return direction === 'asc' ? (
        <ChevronUpIcon className="h-4 w-4 ml-1 inline" />
    ) : (
        <ChevronDownIcon className="h-4 w-4 ml-1 inline" />
    );
}

/**
 * Historical value table component displaying ledger value data in tabular format.
 * Columns: Period | Value | Change | Change %
 * Features sortable columns with sort indicators.
 */
export function HistoricalValueTable({ items, currency, isDebt = false }: HistoricalValueTableProps) {
    const [sortField, setSortField] = useState<SortField>('period');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    // Compute change values for each item
    const itemsWithChange = useMemo(() => {
        return items.map((item, index) => {
            const previousItem = index > 0 ? items[index - 1] : null;
            const previousValue = previousItem?.value ?? null;

            // Calculate value change
            const valueChange =
                item.value !== null && previousValue !== null
                    ? item.value - previousValue
                    : null;

            // Calculate growth percentage
            const growthPercentage =
                valueChange !== null && previousValue !== null && previousValue !== 0
                    ? (valueChange / Math.abs(previousValue)) * 100
                    : null;

            return {
                ...item,
                valueChange,
                growthPercentage,
            };
        });
    }, [items]);

    // Sort data
    const sortedItems = useMemo(() => {
        const sorted = [...itemsWithChange];
        sorted.sort((a, b) => {
            let aVal: number | string | null | undefined;
            let bVal: number | string | null | undefined;

            switch (sortField) {
                case 'period':
                    aVal = a.periodEnd;
                    bVal = b.periodEnd;
                    break;
                case 'value':
                    aVal = a.value;
                    bVal = b.value;
                    break;
                case 'change':
                    aVal = a.valueChange;
                    bVal = b.valueChange;
                    break;
                case 'changePercent':
                    aVal = a.growthPercentage;
                    bVal = b.growthPercentage;
                    break;
                default:
                    aVal = a.periodEnd;
                    bVal = b.periodEnd;
            }

            // Handle nulls
            if (aVal === null || aVal === undefined) {
                if (bVal === null || bVal === undefined) return 0;
                return sortDirection === 'asc' ? 1 : -1;
            }
            if (bVal === null || bVal === undefined) return sortDirection === 'asc' ? -1 : 1;

            // Compare
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [itemsWithChange, sortField, sortDirection]);

    // Handle column header click
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // Header button style
    const headerButtonClass = "flex items-center cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors";

    if (items.length === 0) {
        return (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                No historical values yet. Record monthly for best tracking.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={headerButtonClass}
                                onClick={() => handleSort('period')}
                            >
                                Period
                                <SortIcon field="period" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('value')}
                            >
                                Value
                                <SortIcon field="value" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('change')}
                            >
                                Change
                                <SortIcon field="change" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                        <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">
                            <button
                                className={`${headerButtonClass} ml-auto`}
                                onClick={() => handleSort('changePercent')}
                            >
                                Change %
                                <SortIcon field="changePercent" currentField={sortField} direction={sortDirection} />
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedItems.map((item, index) => (
                        <tr
                            key={item.periodEnd}
                            className={`border-b border-gray-100 dark:border-gray-800 ${
                                index % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''
                            }`}
                        >
                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                {item.periodLabel}
                                {item.isCarriedForward && (
                                    <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">*</span>
                                )}
                            </td>
                            <td className="px-4 py-3 text-right">
                                {item.value !== null ? (
                                    <MoneyDisplay
                                        value={item.value}
                                        currency={currency}
                                        size="sm"
                                        className="font-semibold text-gray-900 dark:text-white"
                                    />
                                ) : (
                                    <span className="text-gray-400 dark:text-gray-500">-</span>
                                )}
                            </td>
                            <td className={`px-4 py-3 text-right ${getChangeColorClass(item.valueChange, isDebt)}`}>
                                {item.valueChange !== null && item.valueChange !== undefined ? (
                                    <MoneyDisplay
                                        value={item.valueChange}
                                        currency={currency}
                                        size="sm"
                                        showSign
                                        className="inline"
                                    />
                                ) : (
                                    <span className="text-gray-400 dark:text-gray-500">-</span>
                                )}
                            </td>
                            <td className={`px-4 py-3 text-right ${getChangeColorClass(item.growthPercentage, isDebt)}`}>
                                {formatPercentage(item.growthPercentage)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
